import { useNavigate } from "react-router-dom";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { RowActions } from "@/components/ui/RowActions";
import { formatDate, formatDateTime, initials } from "@/lib/utils";
import type { User } from "../types";
import { StatusBadge } from "./StatusBadge";

interface UserTableProps {
  users: User[];
  isLoading?: boolean;
  onDelete?: (id: string, name: string) => void;
}

export function UserTable({ users, isLoading, onDelete }: UserTableProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="p-12 text-center text-sm text-muted-foreground">
        Loading users…
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="p-12 text-center text-sm text-muted-foreground">
        No users match the current filters.
      </div>
    );
  }

  return (
    <table className="w-full text-sm">
        <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-6 py-3">User</th>
            <th className="px-6 py-3">Roles</th>
            <th className="px-6 py-3">Status</th>
            <th className="px-6 py-3">Last Login</th>
            <th className="px-6 py-3">Created</th>
            <th className="px-6 py-3 text-center">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {users.map((user) => (
            <tr
              key={user.id}
              className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
              onClick={() => navigate(`/admin/users/${user.id}`)}
            >
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <Avatar src={user.avatarUrl} fallback={initials(user.name)} />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-foreground">{user.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                    {user.code && (
                      <div className="truncate font-mono text-[10px] text-muted-foreground">{user.code}</div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex flex-wrap gap-1.5">
                  {user.roles.length === 0 ? (
                    <span className="text-xs text-muted-foreground">No roles</span>
                  ) : (
                    user.roles.map((role) => (
                      <Badge key={role.id} variant="default">
                        {role.name}
                      </Badge>
                    ))
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <StatusBadge status={user.status} />
              </td>
              <td className="px-6 py-4 text-muted-foreground">
                {formatDateTime(user.lastLoginAt)}
              </td>
              <td className="px-6 py-4 text-muted-foreground">
                {formatDate(user.createdAt)}
              </td>
              <td className="px-6 py-4">
                <RowActions
                  onView={() => navigate(`/admin/users/${user.id}`)}
                  onEdit={() => navigate(`/admin/users/${user.id}?edit=true`)}
                  onDelete={() => {
                    if (onDelete) onDelete(user.id, user.name);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
  );
}
